import axios from 'axios'
import memoize from 'memoizee'
import RealTimeEvents, { RealtimeEvent } from './RealTimeEvents'
import { APIConfig } from 'types/definitions'
import { switchMap } from 'rxjs/operators'
import { Observable } from 'rxjs'

/**
 * Memoized event manager factory.
 */
const getEventsManager = memoize(function getEventsManager(
  host: string,
  token: string,
  globalParams?: Record<string, unknown>,
) {
  return new RealTimeEvents().connect(host, token, globalParams)
})

/**
 * API client base.
 */
export class APIClient {
  config: APIConfig
  token: string | null = null

  /**
   * Axios client.
   */
  readonly client

  /**
   * Constructor.
   *
   * @param {Function} config.getToken
   * @param {string} config.baseURL
   */
  constructor(config: APIConfig) {
    if (!config) {
      throw new Error('No config given.')
    }

    if (!config.baseURL) {
      throw new Error('No config.baseURL given.')
    }

    this.config = config

    this.config.token$.subscribe((token) => (this.token = token))
    this.client = axios.create({
      baseURL: this.config.baseURL.startsWith('/')
        ? `${window.location.origin}${this.config.baseURL}`
        : this.config.baseURL,
    })
    this.client.interceptors.request.use((config) => {
      const token = this.token
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      if (this.config.globalParams) {
        config.params ??= {}
        Object.assign(config.params, this.config.globalParams)
      }
      return config
    })
  }

  /**
   * Don't call this until we have a token.
   */
  when<T extends RealtimeEvent>(event: T['type']) {
    return this.config.token$.pipe(
      switchMap(
        (token) =>
          new Observable<T>((observer) => {
            // `getEventsManager` caches based on host and token, so we don't need
            // to do any caching here.
            const manager = getEventsManager(
              this.client.defaults.baseURL!,
              token,
              this.config.globalParams,
            )
            return manager.subscribe<T>(event, (e) => observer.next(e))
          }),
      ),
    )
  }

  /**
   * Helper to extract data.
   */
  _data<T>(resp: { data: T }) {
    return resp.data
  }
}
